import React, { useState, useEffect } from 'react';
import Button from '../Button/Button';
import LibelleNouveauMarche from '../LibelleNouveauMarche/LibelleNouveauMarche';
import SearchBar from '../SearchBar/SearchBar'; // NOUVEAU: Import du composant SearchBar
import ArticlesService, { Article } from '../../services/articlesService';
import { formatPrixByLot, calculateTotalHT, formatPriceFR } from '../../utils/priceUtils';
import Banner from '../Banner/Banner';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus.svg';
import './ModalArticles.css';

// Explication de la grille tarifaire, cote ACHETEUR. Le catalogue reprend une regle de tarification
// que le prestataire renseigne a la soumission ("il indique 3 prix...") ; on la reformule ici du point
// de vue de celui qui LIT les prix et commande. Contenu generique (hors le prix, propre a l'article),
// d'ou une constante hissee hors du composant plutot qu'un JSX reconstruit a chaque rendu.
// Passe en `message` a Banner, qui enveloppe desormais dans un <div> (et non plus un <p>) : ce
// paragraphe peut donc rester un <p>, ce qu'il est. Le contourner en <div> ne faisait que deplacer
// l'imbrication invalide (<div> dans <p>) au lieu de la supprimer.
const GrillePrixInfoAcheteur: React.FC = () => (
  <>
    <p className="grille-prix__info-p">
      Chaque article comporte deux seuils de quantité (A et B). Le prix unitaire appliqué dépend de la
      quantité que vous commandez : en dessous du seuil A, entre les seuils A et B, ou au-dessus du seuil B.
      Pour l'unité <strong>« € (budget) »</strong>, le prix unitaire est un pourcentage du budget de
      l'opération, exprimé sous la forme 0,XX € : par exemple, 0,05 € correspond à 5 % d'honoraires sur
      le budget de l'opération.
    </p>
  </>
);

/**
 * Formater un prix en fonction de l'unité et du lot technique
 * - Pour lot 105 avec unité "€ (Budget)" : 4 décimales
 * - Sinon : 2 décimales
 * @deprecated Utiliser formatPrixByLot de priceUtils.tsx à la place
 */
const formatPrix = (prix: number, unite?: string, idLotTechnique?: number, withCurrency: boolean = true): string => {
  return formatPrixByLot(prix, unite, idLotTechnique, withCurrency);
};

// Quantite (entier) : affichage FR avec espace en separateur de milliers (ex. "10 000 000").
const formatQuantite = (n: number): string => (n || 0).toLocaleString('fr-FR');

// Parse d'une saisie de quantite : on ne garde QUE les chiffres (aucune lettre/symbole accepte).
const parseQuantite = (raw: string): number => parseInt(raw.replace(/\D/g, ''), 10) || 0;

// Borne de tranche. Un seuil absent (`null`) n'est pas un zero : la tranche n'est pas bornee.
const formatSeuil = (seuil?: number | null): string =>
  seuil === null || seuil === undefined ? '—' : formatQuantite(Number(seuil));

// Reference STABLE pour le defaut de `existingSelection`. IMPERATIF : un defaut `= []` ecrit dans la
// signature recree un tableau A CHAQUE RENDU ; comme il est dans les deps de l'effet de chargement,
// tout appelant qui ne fournit pas la prop declenchait un rechargement en boucle. Le defaut est donc
// hisse hors du composant, une fois pour toutes (les appelants n'ont plus a contourner le piege).
const EMPTY_SELECTION: Article[] = [];

interface ModalArticlesProps {
  isOpen: boolean;
  onClose: () => void;
  commandes: any[];
  lotNom: string;
  onAddToCommand: (articles: Article[]) => void;
  existingSelection?: Article[];
  prestataireRang: number;
  // Mode "sans bon de commande prerequis" (page Catalogue) : au lieu de sauver les articles dans une
  // operation existante, on delegue la validation au parent (qui cree le BC puis attache). Quand fourni,
  // il remplace la sauvegarde interne (aucune operation requise cote modale).
  onValidateExternal?: (articles: Article[]) => Promise<void> | void;
  // Libelle du bouton de validation (defaut "Ajouter (n)").
  validateLabel?: string;
  // Titre de la modale. Defaut "Catalogue - {lotNom}" (contexte BC, ou l'on pioche dans le catalogue).
  // La page Catalogue le remplace : elle affiche le lot technique ET le lot geographique.
  title?: string;
  // Mode Catalogue : aucun lot geographique choisi -> l'etat vide invite a en selectionner un
  // (au lieu de "Aucun article disponible pour ce catalogue").
  awaitingSelection?: boolean;
  // Message affiche quand le catalogue est vide (hors recherche et hors `awaitingSelection`). La page
  // Catalogue l'utilise pour expliquer POURQUOI : lot infructueux, ou lot dont la consultation est en cours.
  emptyMessage?: React.ReactNode;
  // Page Catalogue : la modale garde son rendu COMPLET (quantites, total par article, total en pied),
  // mais la selection n'est PAS transmise a la suite du flux. Consequence unique : le bouton de
  // validation n'exige plus qu'un article soit selectionne.
  ignoreSelection?: boolean;
  // Contexte composition acheteur : si fourni, un bouton "Laisser le prestataire remplir les articles"
  // est rendu a cote de la recherche (le parent decide de l'action : fermer + ouvrir la modale presta).
  onLaisserPrestataire?: () => void;
  laisserPrestataireLabel?: string;
  // Page Catalogue (mode "browse") : remplacent le bouton "Commander" par deux actions de creation.
  // Fournis avec `ignoreSelection`, ils basculent la modale en consultation pure : plus de quantite ni
  // de total, la ligne entiere (hors "Details") declenche la creation d'un bon de commande.
  onNouveauBonCommande?: () => void;
  onNouveauMarcheSubsequent?: () => void;
  // Contexte BC acheteur : saisie d'un code de chiffrage (devis prestataire pre-etabli) dans la barre
  // de recherche. Opt-in comme `onLaisserPrestataire` : sans `enableCodeChiffrage`, aucun champ n'est
  // rendu et le comportement des autres usages (Catalogue, prestataire...) est strictement inchange.
  // `onAppliquerCode` resout le code cote parent (applyCode + rechargement) ; il peut throw pour signaler
  // un echec metier (code inconnu / mauvais lot-departement / mauvais rang), affiche alors sous la rangee.
  enableCodeChiffrage?: boolean;
  onAppliquerCode?: (code: string) => Promise<void> | void;
  // Rendu INTEGRE (page /chiffrage) : STRICTEMENT le meme catalogue, mais pose dans la page au lieu
  // d'une modale. On retire seulement l'habillage de modale (overlay, entete, pied) ; le parent porte
  // les siens. Opt-in : sans ce prop, le rendu est celui de la modale, inchange.
  inline?: boolean;
  // Remonte la selection courante (articles + quantites) a chaque changement. Indispensable en mode
  // `inline`, ou c'est le PARENT qui persiste (le composant n'a plus de bouton de validation).
  onSelectionChange?: (articles: Article[]) => void;
  // Quantites verrouillees (ex. chiffrage fige par un code) : le catalogue reste lisible, non editable.
  readOnly?: boolean;
  // Contenu libre ajoute a droite de la recherche (ex. rappel des lots chiffres).
  searchExtra?: React.ReactNode;
  // Mode `inline` : actions du parent, posees dans le PIED DU CATALOGUE a la place d'Annuler/Valider.
  // Elles partagent ainsi la barre du total (`space-between`, centre verticalement).
  footerActions?: React.ReactNode;
  // APERCU : ne rend que les N premieres lignes, suivies d'un "+ X autres articles". La recherche est
  // masquee (un apercu ne se fouille pas). A combiner avec `inline` + `readOnly`. Sert a remplacer
  // l'ancien CataloguePreview, qui recopiait les lignes de ce composant.
  previewLimit?: number;
}

const ModalArticles: React.FC<ModalArticlesProps> = ({
  isOpen,
  onClose,
  commandes,
  lotNom,
  onAddToCommand,
  existingSelection = EMPTY_SELECTION,
  prestataireRang,
  onValidateExternal,
  validateLabel,
  title,
  awaitingSelection = false,
  ignoreSelection = false,
  emptyMessage,
  onLaisserPrestataire,
  laisserPrestataireLabel = 'Laisser le prestataire remplir les articles',
  onNouveauBonCommande,
  onNouveauMarcheSubsequent,
  enableCodeChiffrage = false,
  onAppliquerCode,
  inline = false,
  onSelectionChange,
  readOnly = false,
  searchExtra,
  footerActions,
  previewLimit,
}) => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<Article[]>([]); // NOUVEAU: Articles filtrés
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedArticle, setExpandedArticle] = useState<string | null>(null);
  const [articleDetails, setArticleDetails] = useState<{ [key: string]: Article }>({});
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState(''); // NOUVEAU: Terme de recherche
  // Ligne dont le champ quantite est en cours d'edition : chiffres bruts pendant la saisie
  // (pas de saut de curseur), formatage avec espaces au blur.
  const [editingQtyId, setEditingQtyId] = useState<string | null>(null);
  // Code de chiffrage (contexte BC acheteur, opt-in) : saisie, application en cours, erreur metier locale.
  const [codeChiffrage, setCodeChiffrage] = useState('');
  const [applyingCode, setApplyingCode] = useState(false);
  const [codeChiffrageError, setCodeChiffrageError] = useState<string | null>(null);
  // const [searchLoading, setSearchLoading] = useState(false); // NOUVEAU: État de chargement de recherche

  // MODIFIÉ: Fonction pour filtrer en front-end
const filterArticlesLocally = (searchTerm: string) => {
  if (!searchTerm.trim()) {
    // Si pas de terme de recherche, afficher tous les articles
    setFilteredArticles(articles);
    return;
  }

  // Normaliser le terme de recherche (minuscules, sans accents)
  const normalizeString = (str: string) => {
    return str
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  };

  const searchTerms = normalizeString(searchTerm).split(' ').filter(term => term.length > 0);

  // Filtrer les articles
  const filtered = articles.filter(article => {
    const searchableText = normalizeString(
      `${article.code} ${article.nom} ${article.description}`
    );

    // Vérifier que tous les termes de recherche sont présents
    return searchTerms.every(term => searchableText.includes(term));
  });

  setFilteredArticles(filtered);
};

// MODIFIÉ: useEffect pour filtrage en temps réel (sans debounce nécessaire car pas d'API)
useEffect(() => {
    filterArticlesLocally(searchTerm);
}, [searchTerm, articles]);

// MODIFIÉ: Fonction pour gérer le changement de terme de recherche
const handleSearch = (value: string) => {
  setSearchTerm(value);
};

// Fonction pour effacer la recherche
const handleClearSearch = () => {
  setSearchTerm('');
  setFilteredArticles(articles);
};

// Applique le code de chiffrage saisi via le handler parent (resolution + rechargement des articles).
// Le parent peut throw pour un echec metier (code inconnu / mauvais lot / mauvais rang) : on l'affiche
// sous la rangee de recherche sans casser la modale ni le catalogue deja charge.
const handleAppliquerCode = async () => {
  const code = codeChiffrage.trim();
  if (!onAppliquerCode || !code || applyingCode) return;
  setApplyingCode(true);
  setCodeChiffrageError(null);
  try {
    await onAppliquerCode(code);
    setCodeChiffrage('');
  } catch (err) {
    setCodeChiffrageError(err instanceof Error ? err.message : "Impossible d'appliquer ce code de chiffrage.");
  } finally {
    setApplyingCode(false);
  }
};

  // NOUVEAU: Fonction pour rechercher dans l'API
  // const searchArticles = async (searchTerm: string) => {
  //   if (!searchTerm.trim()) {
  //     // Si pas de terme de recherche, afficher tous les articles
  //     setFilteredArticles(articles);
  //     return;
  //   }

  //   setSearchLoading(true);
  //   try {
  //     const token = localStorage.getItem('token');
  //     if (!token) {
  //       throw new Error('Token d\'authentification manquant');
  //     }

  //     console.log('Recherche d\'articles avec le terme:', searchTerm);

  //     // CORRECTION: Extraire les IDs de contrat depuis les commandes
  //     const ids = commandes.map(cmd => {
  //       if (cmd.id_contrat) {
  //         return cmd.id_contrat;
  //       }
  //       if (cmd.contrat?.id) {
  //         return cmd.contrat.id;
  //       }
  //       if (cmd.operation && commandes.length === 1) {
  //         const operationCommandes = cmd.operation.commandes || [];
  //         return operationCommandes.map((opCmd: any) => opCmd.id_contrat).filter(Boolean);
  //       }
  //       return null;
  //     }).flat().filter(Boolean);

  //     const idsString = Array.from(new Set(ids)).join(',');
  //     console.log('IDs de contrats pour la recherche:', idsString);

  //     // CORRECTION: Construire l'URL avec les deux paramètres
  //     const response = await fetch(
  //       `${process.env.REACT_APP_API_URL}/bdc/catalogue/prix?catalogue_prix_id_lot_contrat=${idsString}&catalogue_nom=${encodeURIComponent(searchTerm)}`,
  //       {
  //         headers: {
  //           'Authorization': `Bearer ${token}`,
  //           'Content-Type': 'application/json',
  //           'Accept': 'application/json'
  //         }
  //       }
  //     );

  //     if (!response.ok) {
  //       throw new Error(`Erreur HTTP: ${response.status}`);
  //     }

  //     const data = await response.json();
  //     console.log('Résultats de recherche:', data);

  //     if (data.status === 'success' && data.data && data.data.data) {
  //       // CORRECTION: Formater les résultats selon la nouvelle structure avec pagination
  //       const searchResults: Article[] = data.data.data.map((item: any) => {
  //         // Utiliser la première commande pour les informations de contexte
  //         const firstCommande = commandes[0];
  //         let departementId = '00';
  //         let lotTechniqueId = '000';

  //         if (firstCommande) {
  //           // CORRECTION: Extraire le département avec la même logique que articlesService
  //           if (firstCommande.operation?.lot_departement?.departement?.id) {
  //             departementId = firstCommande.operation.lot_departement.departement.id.toString().padStart(2, '0');
  //           } else if (firstCommande.lot_departement?.departement?.id) {
  //             departementId = firstCommande.lot_departement.departement.id.toString().padStart(2, '0');
  //           } else if (firstCommande.operation?.id_lot_departement) {
  //             departementId = firstCommande.operation.id_lot_departement.toString().padStart(2, '0');
  //           } else if (firstCommande.id_lot_departement) {
  //             departementId = firstCommande.id_lot_departement.toString().padStart(2, '0');
  //           }

  //           // CORRECTION: Prioriser l'id_lot_technique de l'article, puis celui de l'opération
  //           if (item.id_lot_technique) {
  //             lotTechniqueId = item.id_lot_technique.toString();
  //           } else if (firstCommande.operation?.id_lot_technique) {
  //             lotTechniqueId = firstCommande.operation.id_lot_technique.toString();
  //           } else if (firstCommande.id_lot_technique) {
  //             lotTechniqueId = firstCommande.id_lot_technique.toString();
  //           } else if (firstCommande.operation?.lot_technique?.id) {
  //             lotTechniqueId = firstCommande.operation.lot_technique.id.toString();
  //           } else if (firstCommande.contrat?.lot_technique?.id) {
  //             lotTechniqueId = firstCommande.contrat.lot_technique.id.toString();
  //           } else if (firstCommande.lot_technique?.id) {
  //             lotTechniqueId = firstCommande.lot_technique.id.toString();
  //           }
  //         }

  //         // Générer la référence
  //         const reference = `${departementId} ${lotTechniqueId} ${item.code || ''} ${prestataireRang}`;

  //         // CORRECTION: Trouver le prix correspondant au rang du prestataire
  //         const prixData = item.prix ?
  //           item.prix.find((prix: any) => parseInt(prix.rang) === prestataireRang) || item.prix[0]
  //           : null;

  //         return {
  //           id: item.id?.toString() || '',
  //           code: reference,
  //           nom: item.nom || '',
  //           description: item.description || '',
  //           prix: prixData ? parseFloat(prixData.prix_a) || 0 : 0,
  //           unite: item.unite || 'Unité',
  //           id_lot_technique: item.id_lot_technique, // ✅ Conserver id_lot_technique pour formatage des prix
  //           quantite: 0,
  //           totalHT: 0,
  //           // CORRECTION: Calculer les prix dégressifs avec la nouvelle structure
  //           prixDegressifs: prixData ? {
  //             palier1: {
  //               min: 1,
  //               max: item.seuil_a || 5,
  //               prix: parseFloat(prixData.prix_a) || 0
  //             },
  //             palier2: {
  //               min: (item.seuil_a || 5) + 1,
  //               max: item.seuil_b || 10,
  //               prix: parseFloat(prixData.prix_b) || 0
  //             },
  //             palier3: {
  //               min: (item.seuil_b || 10) + 1,
  //               prix: parseFloat(prixData.prix_c) || 0
  //             }
  //           } : undefined
  //         };
  //       });

  //       // Restaurer les quantités existantes pour les articles trouvés
  //       const searchResultsWithQuantities = searchResults.map(article => {
  //         const existing = existingSelection.find(existing =>
  //           existing.id === article.id ||
  //           existing.catalogueId === article.id ||
  //           existing.code === article.code
  //         );

  //         if (existing) {
  //           return {
  //             ...article,
  //             quantite: existing.quantite || 0,
  //             totalHT: existing.totalHT || 0
  //           };
  //         }
  //         return article;
  //       });

  //       console.log('Articles de recherche formatés:', searchResultsWithQuantities);
  //       setFilteredArticles(searchResultsWithQuantities);
  //     } else {
  //       setFilteredArticles([]);
  //     }
  //   } catch (error) {
  //     console.error('Erreur lors de la recherche:', error);
  //     setError('Erreur lors de la recherche des articles');
  //     setFilteredArticles([]);
  //   } finally {
  //     setSearchLoading(false);
  //   }
  // };

  // // NOUVEAU: Gestionnaire de recherche avec debounce
  // useEffect(() => {
  //   const timeoutId = setTimeout(() => {
  //     searchArticles(searchTerm);
  //   }, 500); // Debounce de 500ms

  //   return () => clearTimeout(timeoutId);
  // }, [searchTerm, articles, existingSelection]);

  // REMPLACER le useEffect existant (lignes 160-214) par celui-ci :
  useEffect(() => {
    const loadCatalogueArticles = async () => {
      if (!isOpen) return;

      // Aucun lot selectionne (commande synthetique vide, ex. reset du lot geo cote Catalogue) :
      // vider le catalogue -> l'etat vide (invite a selectionner un lot) reapparait et le bouton de
      // validation se regrise (selectedCount == 0), au lieu de garder les anciens articles affiches.
      if (commandes.length === 0) {
        setArticles([]);
        setFilteredArticles([]);
        setError(null);
        setLoading(false);
        return;
      }

      // NOUVEAU: Réinitialiser la recherche lors de l'ouverture du modal
      setSearchTerm('');
      setLoading(true);
      setError(null);

      try {

        // CORRECTION: Vérifier si c'est un contexte BC via plusieurs indicateurs
        const isBC = (() => {
          // 0. Catalogue acheteur (sans BC prerequis) : la commande synthetique porte `_catalogueLotQuery`
          // (lot technique + departement). Pas d'id_contrat -> il faut router vers la branche BC quand meme,
          // sinon le chargement retombe en logique MS (lotIds vide) et rien ne se charge.
          const hasCatalogueLotQuery = commandes.some(cmd => cmd._catalogueLotQuery !== undefined);
          if (hasCatalogueLotQuery) return true;

          // 1. Vérifier le marqueur prestataire
          const hasPrestataireMark = commandes.some(cmd => cmd._isPrestataireContext === true);
          if (hasPrestataireMark) return true;

          // 2. Vérifier si c'est dans le contexte d'une opération transférée
          const hasTransferredOp = commandes.some(cmd => cmd.operation?.transferred === true);
          if (hasTransferredOp) return true;

          // 3. Vérifier si l'URL contient 'bon-commande' (pour les prestataires ET acheteurs BC)
          const urlContainsBonCommande = window.location.pathname.includes('bon-commande');
          if (urlContainsBonCommande) return true;

          // 4. Vérifier si les commandes ont la structure BC
          const hasBCStructure = commandes.some(cmd => cmd.id_contrat !== undefined);
          if (hasBCStructure) return true;

          return false;
        })();


        if (isBC) {
          // CORRECTION: Utiliser l'API BC avec la même logique que pour les acheteurs
          const articlesData = await ArticlesService.getArticlesByCommandes(commandes);

          const articlesWithCorrectPrices = articlesData.map(article => {
            return {
              ...article,
              quantite: 0,
              totalHT: 0
            };
          });

          // Restaurer les quantités existantes avec un matching plus robuste
          const articlesWithQuantities = articlesWithCorrectPrices.map(article => {
            const existing = existingSelection.find(existing =>
              existing.id === article.id ||
              existing.catalogueId === article.id ||
              existing.code === article.code
            );

            if (existing) {
              // Le total est RECALCULE depuis le catalogue et non repris de `existing.totalHT` : le
              // prix depend du palier, donc de la quantite, et le catalogue en est la source de
              // verite. Un appelant peut d'ailleurs ne transmettre que {id, quantite} (cas du
              // chiffrage, qui ne stocke aucun prix) -> se fier au totalHT transmis donnait 0.
              const quantite = existing.quantite || 0;
              const prixUnitaire = ArticlesService.getPrixByQuantity(article, quantite);
              return {
                ...article,
                quantite,
                totalHT: calculateTotalHT(prixUnitaire, quantite)
              };
            }
            return article;
          });

          setArticles(articlesWithQuantities);
          setFilteredArticles(articlesWithQuantities);

        } else {
          // Pour les MS, utiliser l'ancienne logique
          const firstCommande = commandes[0];
          const lotIds = [firstCommande.id_contrat || firstCommande.contrat?.id].filter(id => id);

          if (lotIds.length > 0) {
            const articlesData = await ArticlesService.getArticlesByLot(lotIds);

            const articlesWithCorrectPrices = articlesData.map(article => {
              return {
                ...article,
                quantite: 0,
                totalHT: 0
              };
            });

            // Restaurer les quantités existantes (total recalcule depuis le catalogue, cf. plus haut).
            const articlesWithQuantities = articlesWithCorrectPrices.map(article => {
              const existing = existingSelection.find(existing =>
                existing.id === article.id ||
                existing.catalogueId === article.id ||
                existing.code === article.code
              );

              if (existing) {
                const quantite = existing.quantite || 0;
                const prixUnitaire = ArticlesService.getPrixByQuantity(article, quantite);
                return {
                  ...article,
                  quantite,
                  totalHT: calculateTotalHT(prixUnitaire, quantite)
                };
              }
              return article;
            });

            setArticles(articlesWithQuantities);
            setFilteredArticles(articlesWithQuantities);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement des articles:', error);
        setError(error instanceof Error ? error.message : 'Erreur lors du chargement des articles');
      } finally {
        setLoading(false);
      }
    };

    loadCatalogueArticles();
  }, [isOpen, commandes, existingSelection, prestataireRang]);

  // Remonte la selection au parent des qu'elle change (mode `inline` : c'est lui qui persiste).
  // Volontairement sur `articles` seul : `onSelectionChange` n'est pas dans les deps pour ne pas
  // dependre de la stabilite du callback cote appelant.
  useEffect(() => {
    if (onSelectionChange) onSelectionChange(articles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [articles]);

  // Fonction pour gérer le changement de quantité
  const handleQuantityChange = (articleId: string, newQuantity: number) => {
    const quantity = Math.max(0, newQuantity);

    // Mettre à jour les deux états séparément
    setArticles(prevArticles => 
      prevArticles.map(article => {
        if (article.id === articleId) {
          const prixUnitaire = ArticlesService.getPrixByQuantity(article, quantity);
          // ✅ Utiliser la fonction utilitaire pour garantir la précision à 4 décimales
          const totalHT = calculateTotalHT(prixUnitaire, quantity);
          return {
            ...article,
            quantite: quantity,
            totalHT
          };
        }
        return article;
      })
    );

    setFilteredArticles(prevFiltered =>
      prevFiltered.map(article => {
        if (article.id === articleId) {
          const prixUnitaire = ArticlesService.getPrixByQuantity(article, quantity);
          const totalHT = calculateTotalHT(prixUnitaire, quantity);
          return {
            ...article,
            quantite: quantity,
            totalHT
          };
        }
        return article;
      })
    );
  };

  // Fonction pour diminuer la quantité
  const decreaseQuantity = (articleId: string) => {
    const article = filteredArticles.find(a => a.id === articleId);
    if (article && article.quantite > 0) {
      handleQuantityChange(articleId, article.quantite - 1);
    }
  };

  // Fonction pour augmenter la quantité
  const increaseQuantity = (articleId: string) => {
    const article = filteredArticles.find(a => a.id === articleId);
    if (article) {
      handleQuantityChange(articleId, article.quantite + 1);
    }
  };

  // Fonction pour afficher/masquer les détails
  const toggleDetails = async (articleId: string) => {
    if (expandedArticle === articleId) {
      setExpandedArticle(null);
      return;
    }

    setExpandedArticle(articleId);

    // if (!articleDetails[articleId]) {
    //   try {
    //     const details = await ArticlesService.getArticleDetails(articleId);
    //     if (details) {
    //       setArticleDetails(prev => ({
    //         ...prev,
    //         [articleId]: details
    //       }));
    //     }
    //   } catch (error) {
    //     console.error('Erreur lors du chargement des détails:', error);
    //   }
    // }
  };

  // MODIFIÉ: Fonction pour sauvegarder les articles en BDD avec détection du contexte
  const saveArticlesToDatabase = async (selectedArticles: Article[]) => {
    try {
      // NOUVEAU: Déterminer si c'est un prestataire ou un acheteur
      const isPrestataire = (() => {
        // 1. Vérifier si on a le marqueur prestataire dans les commandes
        const hasPrestataireMark = commandes.some(cmd => cmd._isPrestataireContext === true);
        if (hasPrestataireMark) {
          return true;
        }

        // 2. Vérifier l'URL - si elle contient 'bon-commande' ET que ce n'est pas dans /operation-ms/
        const urlContainsBonCommande = window.location.pathname.includes('bon-commande');
        const urlNotInOperations = !window.location.pathname.includes('operations');
        if (urlContainsBonCommande && urlNotInOperations) {
          return true;
        }

        // 3. Vérifier si les données de commande indiquent un prestataire transféré
        const hasTransferredOperation = commandes.some(cmd => cmd.operation?.transferred === true);
        if (hasTransferredOperation) {
          return true;
        }

        return false;
      })();


      // CORRECTION: Extraire l'ID selon le contexte
      let entityId;
      if (isPrestataire) {
        // Pour les prestataires, utiliser l'ID de la commande
        // 1. Essayer depuis l'URL bon-commande
        const pathParts = window.location.pathname.split('/');
        const bonCommandeIndex = pathParts.indexOf('bon-commande');
        if (bonCommandeIndex !== -1 && pathParts[bonCommandeIndex + 1]) {
          entityId = pathParts[bonCommandeIndex + 1];
        }
        // 2. Fallback: utiliser l'ID depuis les données de commande
        if (!entityId && commandes[0]) {
          entityId = commandes[0].id;
        }
      } else {
        // Pour les acheteurs, utiliser l'ID de l'opération depuis l'URL
        const pathParts = window.location.pathname.split('/');
        if (pathParts.includes('operations')) {
          const operationsIndex = pathParts.indexOf('operations');
          entityId = pathParts[operationsIndex + 1];
        } else {
          // Fallback: essayer de récupérer depuis les commandes
          entityId = commandes[0]?.operation?.id || commandes[0]?.id_operation;
        }
      }

      if (!entityId) {
        throw new Error('ID de l\'entité non trouvé');
      }

      // Préparer le payload selon le format attendu par l'API
      const payload = {
        articles: selectedArticles.map(article => ({
          // id_catalogue: Number(article?.prix?.id), // <-- important : bigint / number
          id_catalogue: Number(article.id),
          quantity: article.quantite
        }))
      };


      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Token d\'authentification non trouvé');
      }

      // CORRECTION: Utiliser l'endpoint approprié selon le contexte
      const endpoint = isPrestataire
        ? `/bdc/commande/${entityId}/catalogue`
        : `/bdc/operation/${entityId}/catalogue`;


      const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erreur HTTP: ${response.status}`);
      }

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des articles:', error);
      throw error;
    }
  };

  // MODIFIÉ: Fonction pour ajouter à la commande avec sauvegarde en BDD
  const handleAddToCommand = async () => {
    // ✅ CORRECTION: Utiliser `articles` au lieu de `filteredArticles` pour avoir TOUS les articles sélectionnés
    const selectedArticles = articles.filter(article => article.quantite > 0);

    // Page Catalogue : la selection n'est pas transmise, le bouton ne fait qu'ouvrir la suite du flux.
    if (ignoreSelection) {
      setSaving(true);
      setError(null);
      try {
        await onValidateExternal?.(selectedArticles);
      } catch (error) {
        setError(error instanceof Error ? error.message : 'Erreur');
      } finally {
        setSaving(false);
      }
      return;
    }

    if (selectedArticles.length === 0) {
      alert('Veuillez sélectionner au moins un article.');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Mode Catalogue : pas d'operation existante -> le parent cree le BC + attache les articles.
      if (onValidateExternal) {
        await onValidateExternal(selectedArticles);
        return; // le parent gere la suite (redirection vers le suivi) ; la modale se demonte.
      }
      await saveArticlesToDatabase(selectedArticles);
      onAddToCommand(selectedArticles);
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'ajout des articles:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Calculer le total avec arrondi à 4 décimales
  // ✅ CORRECTION: Utiliser `articles` au lieu de `filteredArticles` pour compter TOUS les articles sélectionnés
  const totalSelection = parseFloat(
    articles
      .filter(article => article.quantite > 0)
      .reduce((sum, article) => sum + article.totalHT, 0)
      .toFixed(4)
  );

  const selectedCount = articles.filter(article => article.quantite > 0).length;

  // Mode Catalogue : un catalogue vide signifie qu'aucun titulaire n'est attributaire du couple
  // lot technique x lot geographique (tout lot_contrat a des prix). Lancer la commande echouerait
  // cote serveur ("pas de prestataire associe") -> on desamorce l'action en amont.
  const catalogueSansTitulaire = ignoreSelection && !loading && !error && articles.length === 0;

  // Fonction pour déterminer si un article est ajouté à la commande
  const isArticleAdded = (article: Article) => article.quantite > 0;

  // Page Catalogue "consultation pure" (browse) : quand un handler de creation est fourni avec
  // `ignoreSelection`, on masque quantites + total et la ligne devient cliquable (= creation d'un BC).
  const browseMode = ignoreSelection && !!(onNouveauBonCommande || onNouveauMarcheSubsequent);

  // APERCU : on ne rend que les N premieres lignes ; le reste est resume par un "+ X autres articles".
  const enApercu = !!previewLimit && previewLimit > 0;
  const articlesRendus = enApercu ? filteredArticles.slice(0, previewLimit) : filteredArticles;
  const articlesMasques = enApercu ? Math.max(0, filteredArticles.length - previewLimit) : 0;

  if (!isOpen) return null;

  return (
    // Mode `inline` : on remplace l'overlay fixe par un simple bloc de page. Le contenu interieur
    // (recherche, lignes, details, grille de prix) reste STRICTEMENT identique a la modale.
    <div className={inline ? 'modal-articles-inline-root' : 'modal-articles-overlay'}>
      <div className="modal-articles">
        {/* Entete : rendue en modale ET en apercu (qui reproduit la modale en miniature). Masquee
            uniquement en page /chiffrage, ou le parent porte deja son propre entete. */}
        {(!inline || enApercu) && (
          <div className="modal-articles-header">
            <h2>{title ?? `Catalogue - ${lotNom}`}</h2>
            {/* Pas de croix en apercu : il n'y a rien a fermer, le clic ouvre la vraie modale. */}
            {!enApercu && (
              <button className="close-button" onClick={onClose} disabled={saving}>×</button>
            )}
          </div>
        )}

        {/* NOUVEAU: Barre de recherche (+ bouton "laisser le presta remplir" en contexte acheteur).
            Rendue aussi en apercu (elle fait partie du visuel reproduit), mais neutralisee par le CSS
            du conteneur : le clic ouvre la modale au lieu de saisir. */}
        <div className="modal-articles-search">
          <div className="modal-articles-search-row">
            <SearchBar
              placeholder="Rechercher une prestation..."
              value={searchTerm}
              onChange={handleSearch}
              onClear={handleClearSearch}
              loading={false}
            />
            {/* Contexte BC acheteur (opt-in) : appliquer un code de chiffrage pour pre-remplir le catalogue. */}
            {enableCodeChiffrage && (
              <div className="modal-articles-code">
                <input
                  type="text"
                  className="modal-articles-code-input"
                  placeholder="Code de chiffrage"
                  value={codeChiffrage}
                  onChange={(e) => setCodeChiffrage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAppliquerCode();
                  }}
                  disabled={applyingCode}
                  aria-label="Code de chiffrage"
                />
                <Button
                  value={applyingCode ? 'Application...' : 'Appliquer le code'}
                  variant="secondary"
                  onClick={handleAppliquerCode}
                  disabled={applyingCode || !codeChiffrage.trim()}
                  className="modal-articles-code-btn"
                />
              </div>
            )}
            {onLaisserPrestataire && (
              <Button
                value={laisserPrestataireLabel}
                variant="secondary"
                onClick={onLaisserPrestataire}
                className="modal-articles-laisser-presta"
              />
            )}
            {/* Contenu libre a droite de la recherche (ex. rappel des lots chiffres, en page). */}
            {searchExtra}
          </div>
          {/* Erreur metier de l'application du code (code inconnu / mauvais lot-departement / mauvais rang). */}
          {enableCodeChiffrage && codeChiffrageError && (
            <Banner
              variant="danger"
              message={codeChiffrageError}
              className="modal-articles-code-error"
            />
          )}
          {/* Aide de lecture des seuils : sous la recherche, UNE fois — et non repetee dans chaque
              bloc "Details". Masquee en mode `inline` (page /chiffrage) : le prestataire connait ses
              propres paliers, l'explication s'adresse a qui LIT les prix. */}
          {!inline && (
            <Banner
              variant="info"
              className="grille-prix__info modal-articles-grille-info"
              title="Comment lire les seuils de prix"
              message={<GrillePrixInfoAcheteur />}
            />
          )}
          {searchTerm && (
            <div className="search-info">
              <span>
                {filteredArticles.length} résultat{filteredArticles.length !== 1 ? 's' : ''}
                {searchTerm && ` pour "${searchTerm}"`}
              </span>
            </div>
          )}
        </div>

        <div className="modal-articles-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Chargement du catalogue...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
              <Button
                value="Réessayer"
                variant="primary"
                size="small"
                onClick={() => {
                  setError(null);
                  const loadArticles = async () => {
                    setLoading(true);
                    try {
                      const articlesData = await ArticlesService.getArticlesByCommandes(commandes);
                      setArticles(articlesData);
                      setFilteredArticles(articlesData);
                    } catch (err) {
                      console.error(err);
                      setError('Impossible de charger les articles du catalogue.');
                    } finally {
                      setLoading(false);
                    }
                  };
                  loadArticles();
                }}
              />
            </div>
          ) : (
            <div className="articles-list">
              {filteredArticles.length === 0 ? (
                <div className="error-state">
                  <p>
                    {searchTerm
                      ? `Aucun article trouvé pour "${searchTerm}"`
                      : awaitingSelection
                        ? 'Sélectionnez un lot géographique dans les filtres pour afficher le catalogue et les prix.'
                        : (emptyMessage ?? 'Aucun article disponible pour ce catalogue.')
                    }
                  </p>
                  {searchTerm && (
                    <Button
                      value="Effacer la recherche"
                      variant="secondary"
                      size="small"
                      onClick={handleClearSearch}
                    />
                  )}
                </div>
              ) : (
                articlesRendus.map((article) => (
                  <div key={article.id} className="article-container">
                    <div
                      className={`article-row ${browseMode ? 'article-row--browse article-row--clickable' : ''} ${isArticleAdded(article) ? 'article-added' : ''}`}
                      onClick={browseMode ? (e) => {
                        // Page Catalogue : toute la ligne cree un bon de commande, sauf le bouton "Details".
                        if ((e.target as HTMLElement).closest('button')) return;
                        onNouveauBonCommande?.();
                      } : undefined}
                      role={browseMode ? 'button' : undefined}
                    >
                      {/* Code article */}
                      <div className="article-code">
                        {article.code}
                      </div>

                      {/* Informations article */}
                      <div className="article-info">
                        <h4 className="article-nom">{article.nom}</h4>
                        {/* Le prix depend du palier, donc de la quantite : d'ou le placeholder tant que
                            rien n'est saisi. Page Catalogue (`ignoreSelection`), la modale sert justement
                            a CONSULTER les prix -> on affiche d'emblee le prix de base (palier 1). */}
                        <p className="article-prix">
                          {article.quantite >= 1 || ignoreSelection ? (
                            <>
                              <span className="prix-value">
                                {formatPrixByLot(ArticlesService.getPrixByQuantity(article, Math.max(article.quantite, 1)), article.unite, article.id_lot_technique, false)} €
                              </span> / {article.unite}
                            </>
                          ) : (
                            <span className="prix-placeholder">
                              Prix affiché après sélection / {article.unite}
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Contrôles de quantité — masqués en mode consultation (page Catalogue).
                          `readOnly` (chiffrage fige par un code) les laisse visibles mais inertes. */}
                      {!browseMode && (
                        <div className="quantity-controls">
                          <button
                            className="quantity-btn decrease"
                            onClick={() => decreaseQuantity(article.id)}
                            disabled={article.quantite <= 0 || saving || readOnly}
                          >
                            −
                          </button>
                          <input
                            type="text"
                            inputMode="numeric"
                            className="quantity-input"
                            value={
                              editingQtyId === article.id
                                ? (article.quantite === 0 ? '' : String(article.quantite))
                                : formatQuantite(article.quantite)
                            }
                            onFocus={() => setEditingQtyId(article.id)}
                            onBlur={() => setEditingQtyId(null)}
                            onChange={(e) => handleQuantityChange(article.id, parseQuantite(e.target.value))}
                            disabled={saving || readOnly}
                          />
                          <button
                            className="quantity-btn increase"
                            onClick={() => increaseQuantity(article.id)}
                            disabled={saving || readOnly}
                          >
                            +
                          </button>
                        </div>
                      )}

                      {/* Total par ligne — masqué en mode consultation (page Catalogue). */}
                      {!browseMode && (
                        <div className="article-total">
                          <div className="total-value">
                            {article.quantite > 0 ? formatPriceFR(article.totalHT, 2) : ''}
                          </div>
                        </div>
                      )}

                      {/* Bouton détails */}
                      <div>
                        <Button
                          value={expandedArticle === article.id ? 'Masquer' : 'Détails'}
                          variant="secondary"
                          size="small"
                          onClick={() => toggleDetails(article.id)}
                          disabled={saving}
                        />
                      </div>
                    </div>

                    {/* Section des détails expandable */}
                    {expandedArticle === article.id && (
                      <div className="article-details-expanded">
                        <div className="details-header">
                          <h5>Détails de l'article</h5>
                        </div>

                        <div className="details-description">
                          <p>{article.description}</p>
                        </div>

                        {/* Bornes des tranches de quantite : elles conditionnent la lecture de la grille
                            tarifaire ci-dessous, d'ou leur rappel juste avant. */}
                        <div className="details-meta">
                          <div>
                            <span className="details-meta-label">Unité</span>
                            <span className="details-meta-value">{article.unite || '—'}</span>
                          </div>
                          <div>
                            <span className="details-meta-label">Seuil A</span>
                            <span className="details-meta-value">{formatSeuil(article.seuilA)}</span>
                          </div>
                          <div>
                            <span className="details-meta-label">Seuil B</span>
                            <span className="details-meta-value">{formatSeuil(article.seuilB)}</span>
                          </div>
                        </div>

                        {articleDetails[article.id]?.detailsComplets && (
                          <div className="details-description">
                            <h6>Informations détaillées</h6>
                            <p>{articleDetails[article.id].detailsComplets?.descriptionLongue}</p>
                          </div>
                        )}

                        {/* Grille tarifaire : une ligne par titulaire visible (rang), trois colonnes de
                            tranche. Cote prestataire, l'API n'en renvoie qu'une — la sienne. */}
                        {article.prixRangs && article.prixRangs.length > 0 ? (
                          <div className="grille-prix">
                            <h6 className="grille-prix__titre">Prix unitaires HT par rang</h6>
                            {/* L'aide de lecture des seuils vit desormais sous la recherche, une seule
                                fois — elle n'est plus repetee dans chaque bloc "Details". */}
                            <div className="grille-prix__scroll">
                              <table className="grille-prix__table">
                                <thead>
                                  <tr>
                                    <th scope="col" className="grille-prix__rang-th"></th>
                                    <th scope="col">Prix par unité pour des unités inférieures au seuil A</th>
                                    <th scope="col">Prix par unité pour des unités comprises entre les seuils A et B</th>
                                    <th scope="col">Prix par unité pour des unités supérieures au seuil B</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {article.prixRangs.map((ligne) => (
                                    <tr key={ligne.idLotContrat ?? ligne.rang}>
                                      <th scope="row" className="grille-prix__rang">
                                        <span className="grille-prix__rang-num">Rang {ligne.rang || '—'}</span>
                                        {ligne.titulaire?.nom && (
                                          <span className="grille-prix__rang-nom">{ligne.titulaire.nom}</span>
                                        )}
                                        {ligne.titulaire?.siret && (
                                          <span className="grille-prix__rang-siret">{ligne.titulaire.siret}</span>
                                        )}
                                      </th>
                                      <td className="grille-prix__prix grille-prix__prix--t1">
                                        {formatPrix(ligne.prixA, article.unite, article.id_lot_technique)}
                                      </td>
                                      <td className="grille-prix__prix">
                                        {formatPrix(ligne.prixB, article.unite, article.id_lot_technique)}
                                      </td>
                                      <td className="grille-prix__prix grille-prix__prix--t3">
                                        {formatPrix(ligne.prixC, article.unite, article.id_lot_technique)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : article.prixDegressifs && (
                          <div className="prix-degressifs">
                            <h6>Prix dégressifs</h6>
                            <div className="prix-table">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Quantité</th>
                                    <th>Prix unitaire</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  <tr>
                                    <td>{ArticlesService.formatPalierLabel(article.prixDegressifs.palier1)}</td>
                                    <td>{formatPrix(article.prixDegressifs.palier1.prix, article.unite, article.id_lot_technique)}</td>
                                  </tr>
                                  <tr>
                                    <td>{ArticlesService.formatPalierLabel(article.prixDegressifs.palier2)}</td>
                                    <td>{formatPrix(article.prixDegressifs.palier2.prix, article.unite, article.id_lot_technique)}</td>
                                  </tr>
                                  <tr>
                                    <td>{ArticlesService.formatPalierLabel(article.prixDegressifs.palier3)}</td>
                                    <td>{formatPrix(article.prixDegressifs.palier3.prix, article.unite, article.id_lot_technique)}</td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}

              {/* Apercu : le reste du catalogue est resume plutot que rendu. */}
              {articlesMasques > 0 && (
                <div className="modal-articles-preview-more">
                  + {articlesMasques} autres articles - cliquez pour tout voir
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pied du catalogue : porte le TOTAL. En modale il porte aussi Annuler/Valider ; en `inline`
            le parent y injecte ses actions. Absent en APERCU : un extrait n'a ni total ni action. */}
        {!enApercu && (
        <div className={`modal-articles-footer ${browseMode ? 'modal-articles-footer--actions-only' : ''}`}>
          {!browseMode && (
            <div className="footer-summary">
              <div className="selected-count">
                {selectedCount} article{selectedCount > 1 ? 's' : ''} sélectionné{selectedCount > 1 ? 's' : ''}
              </div>
              <div className="footer-total">
                Total HT (Rang {prestataireRang}): <span className="total-amount">
                  {/* Format FR : 2 decimales max + espace en separateur de milliers (coherent avec le total par ligne) */}
                  {formatPriceFR(totalSelection, 2)}
                </span>
              </div>
            </div>
          )}

          <div className="footer-actions">
            {inline ? (
              // En page : les actions viennent du parent (code, Supprimer, PDF, Enregistrer, Generer).
              // Ni "Annuler" ni validation ici : aucune modale a fermer, et c'est lui qui persiste.
              footerActions
            ) : (
              <>
            <Button
              value="Annuler"
              variant="secondary"
              onClick={onClose}
              disabled={saving}
            />
            {browseMode ? (
              <>
                {onNouveauBonCommande && (
                  <Button
                    value={<LibelleNouveauMarche forme="BC" />}
                    variant="primary"
                    icon={<PlusIcon />}
                    onClick={() => onNouveauBonCommande()}
                    disabled={saving || catalogueSansTitulaire}
                  />
                )}
                {onNouveauMarcheSubsequent && (
                  <Button
                    value={<LibelleNouveauMarche forme="MS" />}
                    variant="primary"
                    icon={<PlusIcon />}
                    onClick={() => onNouveauMarcheSubsequent()}
                    disabled={saving || catalogueSansTitulaire}
                  />
                )}
              </>
            ) : (
              <Button
                value={saving ? 'Sauvegarde...' : (validateLabel ?? `Ajouter ${selectedCount > 0 ? `(${selectedCount})` : ''}`)}
                variant="primary"
                onClick={handleAddToCommand}
                disabled={saving || (!ignoreSelection && selectedCount === 0) || catalogueSansTitulaire}
              />
            )}
              </>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default ModalArticles;